import { ApiService } from './api.service'

export type ChatMessageType = 'USER' | 'SYSTEM'

export type ChatMessage = {
  id: string
  conversationId: string
  senderUserId?: string | null
  senderName?: string | null
  messageType: ChatMessageType
  systemType?: string | null
  body: string
  isMine: boolean
  createdAt: string
}

export type ChatConversation = {
  id: string
  employeeUserId: string
  employeeName: string
  companyId: string
  lastMessage?: string | null
  lastMessageAt?: string | null
  unreadCount?: number
  createdAt: string
}

export type ChatTemplate = {
  id: string
  label: string
  body: string
}

export class ChatService extends ApiService {
  async getTemplates(): Promise<ChatTemplate[]> {
    const res = await this.handleResponse(
      this.api.get<{ templates: ChatTemplate[] }>('/chat/templates')
    )
    return res.templates
  }

  async listConversations(): Promise<ChatConversation[]> {
    const res = await this.handleResponse(
      this.api.get<{ conversations: ChatConversation[] }>('/chat/conversations')
    )
    return res.conversations
  }

  async getOrCreateConversation(employeeUserId: string): Promise<ChatConversation> {
    return this.handleResponse(
      this.api.post<ChatConversation>('/chat/conversations', { employeeUserId })
    )
  }

  async getMessages(conversationId: string): Promise<ChatMessage[]> {
    const res = await this.handleResponse(
      this.api.get<{ messages: ChatMessage[] }>(
        `/chat/conversations/${conversationId}/messages`
      )
    )
    return res.messages
  }

  async sendMessage(conversationId: string, message: string): Promise<ChatMessage> {
    return this.handleResponse(
      this.api.post<ChatMessage>(`/chat/conversations/${conversationId}/messages`, {
        message,
      })
    )
  }

  async sendToEmployee(employeeUserId: string, message: string): Promise<ChatMessage> {
    return this.handleResponse(
      this.api.post<ChatMessage>(`/chat/employees/${employeeUserId}/messages`, {
        message,
      })
    )
  }

  async sendTemplate(
    employeeUserId: string,
    templateId: string,
    message?: string
  ): Promise<ChatMessage> {
    return this.handleResponse(
      this.api.post<ChatMessage>(`/chat/employees/${employeeUserId}/template`, {
        templateId,
        message,
      })
    )
  }

  async getUnreadCount(): Promise<number> {
    const res = await this.handleResponse(
      this.api.get<{ count: number }>('/chat/unread-count')
    )
    return res.count
  }

  async markConversationRead(conversationId: string): Promise<void> {
    await this.handleResponse(
      this.api.post(`/chat/conversations/${conversationId}/read`)
    )
  }
}

export const chatService = new ChatService()
